/** @file Document schema parts: images, bones, slots, constraints, events, sockets, references, view. */
import { z } from 'zod';
import { prim } from './primitives';

const image = z
  .object({
    name: prim.name,
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    w: z.number().int().positive(),
    h: z.number().int().positive(),
    // `stack`: PSD stacking position (bottom 0; a later import above every image an earlier one placed), written by image.importLayers (CR-020).
    origin: z.object({ psd: z.string(), layerPath: z.string(), x: prim.finite, y: prim.finite, stack: z.number().int().nonnegative().exactOptional() }).strict().optional(),
  })
  .strict();

const bone = z
  .object({
    name: prim.name,
    parent: prim.idOf('bone').nullable(),
    siblingIndex: z.number().int().nonnegative(),
    length: prim.finite.nonnegative(),
    inherit: prim.inherit,
    setup: prim.localTransform,
    kind: z.enum(['socket', 'socketGrip2']).optional(),
    ui: z.object({ color: prim.hex8.optional(), hidden: z.boolean().optional(), locked: z.boolean().optional() }).strict().optional(),
  })
  .strict();

const slot = z
  .object({
    name: prim.name,
    bone: prim.idOf('bone'),
    blend: prim.blend,
    setup: z.object({ color: prim.hex8, dark: prim.hex6.nullable(), attachment: prim.name.nullable() }).strict(),
  })
  .strict();

const ikConstraint = z
  .object({
    type: z.literal('ik'),
    name: prim.name,
    order: z.number().int().nonnegative(),
    bones: z.union([z.tuple([prim.idOf('bone')]), z.tuple([prim.idOf('bone'), prim.idOf('bone')])]),
    target: prim.idOf('bone'),
    mix: prim.unit,
    softness: prim.finite.nonnegative(),
    bendPositive: z.boolean(),
    compress: z.boolean(),
    stretch: z.boolean(),
    uniform: z.boolean(),
  })
  .strict();

const event = z.object({ name: prim.name, int: z.number().int(), float: prim.finite, string: z.string() }).strict();

const socket = z
  .object({ name: prim.name, parentBone: prim.idOf('bone'), bone: prim.idOf('bone'), grip2Bone: prim.idOf('bone').optional(), a: prim.px, b: prim.px })
  .strict();

const reference = z
  .object({ name: prim.name, image: prim.idOf('img'), x: prim.finite, y: prim.finite, scale: prim.finite.positive(), opacity: prim.unit, visible: z.boolean(), locked: z.boolean() })
  .strict();

const view = z
  .object({
    grid: z
      .object({
        mode: z.enum(['off', 'square', 'iso']),
        tileW: z.number().positive(),
        tileH: z.number().positive(),
        isoAngle: z.union([z.literal('ratio'), z.literal(30)]),
        snap: z.boolean(),
        footprint: z.object({ w: z.number().positive(), h: z.number().positive(), offsetX: prim.finite, offsetY: prim.finite }).strict().nullable(),
      })
      .strict(),
  })
  .strict();

export const skeletonParts = { image, bone, slot, constraint: z.discriminatedUnion('type', [ikConstraint]), event, socket, reference, view };

export type ImageDoc = z.infer<typeof image>;
export type BoneDoc = z.infer<typeof bone>;
export type SlotDoc = z.infer<typeof slot>;
export type IkConstraintDoc = z.infer<typeof ikConstraint>;
export type EventDoc = z.infer<typeof event>;
export type SocketDoc = z.infer<typeof socket>;
export type ReferenceDoc = z.infer<typeof reference>;
export type ViewDoc = z.infer<typeof view>;
